import { fmtKg } from "@/lib/utils";
import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Loader2, Scissors } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const COMMON_WIDTHS = [
  63.5, 68.5, 70, 76.2, 78, 79, 86, 90, 95.2, 96.8, 97, 100, 105, 110, 114, 114.3, 120.7, 130, 133,
];

const emptyRow = () => ({ rid: `r_${Math.random().toString(36).slice(2, 10)}`, slit_width_mm: "", count: 1 });

const SlittingPlanner = ({ mother, onCancel, onSubmit, submitting }) => {
  const [rows, setRows] = useState([emptyRow()]);
  const [operator, setOperator] = useState("");
  const [notes, setNotes] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const motherWidth = Number(mother.width_mm) || 0;
  const motherWeight = Number(mother.weight_kg) || 0;
  const wtPerMm = motherWidth ? motherWeight / motherWidth : 0;

  const enriched = useMemo(() =>
    rows.map((r) => {
      const w = Number(r.slit_width_mm) || 0;
      const n = Math.max(0, Number(r.count) || 0);
      const perSlit = wtPerMm ? +(wtPerMm * w).toFixed(2) : 0;
      const total = +(perSlit * n).toFixed(2);
      const widthUsed = +(w * n).toFixed(2);
      return { ...r, w, n, perSlit, total, widthUsed };
    }),
  [rows, wtPerMm]);

  const totalWidthUsed = +enriched.reduce((s, r) => s + r.widthUsed, 0).toFixed(2);
  const totalWeight = +enriched.reduce((s, r) => s + r.total, 0).toFixed(2);
  const totalSfgCount = enriched.reduce((s, r) => s + r.n, 0);
  const widthRemaining = +(motherWidth - totalWidthUsed).toFixed(2);
  const widthPct = motherWidth ? Math.min(100, (totalWidthUsed / motherWidth) * 100) : 0;
  const yieldPct = motherWeight ? +((totalWeight / motherWeight) * 100).toFixed(2) : 0;

  const exceedsWidth = totalWidthUsed > motherWidth + 0.001;
  const hasInvalid = enriched.some((r) => r.w <= 0 || r.n <= 0);
  const canCommit = !submitting && !exceedsWidth && totalSfgCount > 0 && !hasInvalid;

  const set = (i, k, v) => setRows((arr) => arr.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const addRow = () => setRows((arr) => [...arr, emptyRow()]);
  const removeRow = (i) => setRows((arr) => arr.filter((_, idx) => idx !== i));
  const pickWidth = (w) => {
    // fill the first empty slit_width row, else add a new one
    const idx = rows.findIndex((r) => !r.slit_width_mm);
    if (idx >= 0) set(idx, "slit_width_mm", w);
    else setRows((arr) => [...arr, { rid: `r_${Math.random().toString(36).slice(2, 10)}`, slit_width_mm: w, count: 1 }]);
  };

  const handleSubmit = () => {
    if (!canCommit) return;
    onSubmit({
      plan: enriched
        .filter((r) => r.w > 0 && r.n > 0)
        .map((r) => ({ slit_width_mm: r.w, count: r.n })),
      operator: operator.trim(),
      notes: notes.trim(),
    });
  };

  return (
    <div data-testid="slitting-planner" className="space-y-5">
      {/* Mother summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        {[
          ["RM", mother.coil_id],
          ["Supplier", mother.supplier],
          ["Grade", mother.grade],
          ["Thickness", `${mother.thickness_mm} mm`],
          ["Width", `${motherWidth} mm`],
          ["RM Net Wt", `${motherWeight.toLocaleString()} kg`],
          ["kg / mm width", wtPerMm.toFixed(2)],
          ["Location", mother.location || "—"],
        ].map(([k, v]) => (
          <div key={k} className="border border-slate-100 bg-slate-50 px-3 py-2 rounded-sm">
            <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">{k}</p>
            <p className="font-mono-data text-sm text-slate-900 mt-0.5 truncate" data-testid={`mother-${k.toLowerCase().replace(/\s|\//g, '-')}`}>{v}</p>
          </div>
        ))}
      </div>

      {/* Width usage bar */}
      <div data-testid="width-usage" className="bg-white border border-slate-200 rounded-sm p-3 space-y-1.5">
        <div className="flex justify-between text-xs">
          <span className="uppercase tracking-wider text-slate-500 font-semibold">Width consumed</span>
          <span className={`font-mono-data ${exceedsWidth ? "text-red-700 font-bold" : "text-slate-900"}`}>
            {totalWidthUsed} / {motherWidth} mm
            {!exceedsWidth && <span className="text-slate-500"> · {widthRemaining} mm free</span>}
            {exceedsWidth && <span className="text-red-700"> · exceeds by {(-widthRemaining).toFixed(2)} mm</span>}
          </span>
        </div>
        <div className="h-2 bg-slate-100 rounded-sm overflow-hidden">
          <div
            className={`h-full ${exceedsWidth ? "bg-red-500" : "bg-orange-500"} transition-all`}
            style={{ width: `${widthPct}%` }}
          />
        </div>
      </div>

      {/* Quick-pick widths */}
      <div>
        <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold mb-2">Quick pick (mm)</p>
        <div className="flex flex-wrap gap-1.5">
          {COMMON_WIDTHS.map((w, i) => (
            <button
              key={`${w}-${i}`}
              type="button"
              data-testid={`quickpick-${w}`}
              onClick={() => pickWidth(w)}
              className="px-2.5 h-7 text-xs font-mono-data border border-slate-200 hover:bg-slate-900 hover:text-white hover:border-slate-900 rounded-sm transition-colors"
            >
              {w}
            </button>
          ))}
        </div>
      </div>

      {/* Plan table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="bg-slate-50 px-3 py-2 grid grid-cols-12 text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold gap-2">
          <div className="col-span-1">#</div>
          <div className="col-span-3">Slit Width (mm)</div>
          <div className="col-span-2">Count</div>
          <div className="col-span-2 text-right">Per-Slit (kg)</div>
          <div className="col-span-2 text-right">Row Total (kg)</div>
          <div className="col-span-1 text-right">Width</div>
          <div className="col-span-1"></div>
        </div>
        {enriched.map((r, i) => (
          <div key={r.rid || `row-${i}`} data-testid={`plan-row-${i}`} className="px-3 py-2 grid grid-cols-12 gap-2 items-center border-t border-slate-100">
            <div className="col-span-1 text-xs text-slate-500 font-mono-data">{i + 1}</div>
            <div className="col-span-3">
              <Input
                data-testid={`plan-width-${i}`}
                type="number"
                step="0.1"
                min="0"
                value={r.slit_width_mm}
                onChange={(e) => set(i, "slit_width_mm", e.target.value)}
                className="h-9 font-mono-data"
                placeholder="e.g. 100"
              />
            </div>
            <div className="col-span-2">
              <Input
                data-testid={`plan-count-${i}`}
                type="number"
                step="1"
                min="1"
                value={r.count}
                onChange={(e) => set(i, "count", e.target.value)}
                className="h-9 font-mono-data"
              />
            </div>
            <div className="col-span-2 text-right font-mono-data text-sm text-slate-900" data-testid={`plan-per-slit-${i}`}>
              {r.perSlit ? r.perSlit.toFixed(2) : "—"}
            </div>
            <div className="col-span-2 text-right font-mono-data text-sm font-semibold text-slate-900" data-testid={`plan-row-total-${i}`}>
              {r.total ? r.total.toFixed(2) : "—"}
            </div>
            <div className="col-span-1 text-right font-mono-data text-xs text-slate-500">
              {r.widthUsed ? r.widthUsed.toFixed(1) : "—"}
            </div>
            <div className="col-span-1 text-right">
              <button
                data-testid={`remove-plan-row-${i}`}
                onClick={() => removeRow(i)}
                disabled={enriched.length === 1}
                className="text-slate-400 hover:text-red-600 disabled:opacity-30 p-1"
                aria-label="Remove row"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
        <div className="px-3 py-2 border-t border-slate-200 flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={addRow} data-testid="add-plan-row-btn" className="rounded-sm h-8">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add slit row
          </Button>
          <div className="flex items-center gap-6 text-xs">
            <div>
              <span className="text-slate-500 uppercase tracking-wider">Total SFGs</span>{" "}
              <span className="font-mono-data font-bold text-slate-900" data-testid="total-sfg-count">{totalSfgCount}</span>
            </div>
            <div>
              <span className="text-slate-500 uppercase tracking-wider">Total Weight</span>{" "}
              <span className="font-mono-data font-bold text-slate-900" data-testid="total-sfg-weight">{fmtKg(totalWeight)} kg</span>
            </div>
            <div>
              <span className="text-slate-500 uppercase tracking-wider">Yield</span>{" "}
              <span className="font-mono-data font-bold text-slate-900" data-testid="yield-pct">{yieldPct}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Operator + notes */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-semibold tracking-wide uppercase text-slate-600">Operator (optional)</Label>
          <Input data-testid="slit-operator-input" value={operator} onChange={(e) => setOperator(e.target.value)} placeholder="e.g. Rajesh K." />
        </div>
        <div>
          <Label className="text-xs font-semibold tracking-wide uppercase text-slate-600">Notes (optional)</Label>
          <Input data-testid="slit-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Trial run for new die" />
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
        <Button variant="outline" data-testid="cancel-slit-btn" onClick={onCancel} className="rounded-sm h-11">
          Cancel
        </Button>
        <Button
          data-testid="generate-sfgs-btn"
          onClick={() => setConfirmOpen(true)}
          disabled={!canCommit}
          className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm h-11 px-6"
        >
          {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</> : <><Scissors className="h-4 w-4 mr-2" /> Generate {totalSfgCount} SFGs &amp; print</>}
        </Button>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="slit-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Generate {totalSfgCount} SFGs from {mother.coil_id}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will mark the RM as <strong>consumed</strong> and cannot be undone. {totalSfgCount} SFG labels will be generated and opened for printing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-confirm-slit-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-slit-btn"
              onClick={() => { setConfirmOpen(false); handleSubmit(); }}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Yes, slit and print
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SlittingPlanner;
